import { useContext } from 'react';
import { Reference, ResourceInput } from 'sr-types/lib/production/v1/graphql';
import { ContactInput, InquiryInput, MutationResult, ShortlistInput } from 'sr-types/lib/shortlist/v1/graphql';
import { UserContext } from '../common/auth/UserContext';
import { CONSTANTS_ID } from '../common/constants/constants';
import { useMutationWithContext } from '../common/hooks/useMutationWithContext';
import { failedToastMessage, successToastMessage } from '../common/utils/commonUtils';
import useHistory from '../common/utils/useHistory';
import { client as productionClient, saveResourceQuery } from '../production/helpers/production';
import { ResourceInfoForShortlist, resourceStateToShortlist, toInputForSaveResource } from '../resource/resourceUtils';
import { PEOPLE_TYPE, orgInquiryPayload, personInquiryPayload } from '../shortlists/form/inquiry/inquiry';
import { SendInquiries } from '../shortlists/form/inquiry/inquiryApi';
import { useSaveShortlist } from '../shortlists/hooks/useSaveShortlist';
import { saveLineItems, client as shortlistClient } from '../shortlists/shortlist';

const sendInquiryForShortlists = () => {
    const [sendInquiry] = useMutationWithContext(SendInquiries, shortlistClient);

    const sendInquiriesToInvitee = (inquiryInput: InquiryInput[]) => {
        return new Promise<void>((resolve, reject) => {
            sendInquiry({
                variables: { inquiries: inquiryInput }
            })
                .then(() => {
                    resolve();
                    successToastMessage('form.shortlistLineItem.inquiry.success');
                })
                .catch(() => {
                    failedToastMessage('form.shortlistLineItem.inquiry.error');
                    reject();
                });
        });
    };

    return { sendInquiriesToInvitee };
};

const createShortlistLineitems = () => {
    const [saveItems] = useMutationWithContext(saveLineItems, shortlistClient);

    const saveShorlistLineitems = (shortlistId: string, inquiryData, type: string) => {
        const bulkLineitemInput = inquiryData[type].map((p) => {
            return {
                itemReference: p.ref,
                name: p.label
            };
        });
        return new Promise((resolve, reject) => {
            saveItems({
                variables: {
                    shortlistId,
                    input: bulkLineitemInput
                }
            })
                .then((lineitemRes) => {
                    const saveLineItems = lineitemRes.data.saveLineItems;
                    const hasMissingId = saveLineItems?.some((lineitem) => !lineitem.id);
                    if (hasMissingId) {
                        failedToastMessage('shortlist.line.items.save.error');
                        reject(hasMissingId);
                    } else if (saveLineItems?.length === bulkLineitemInput.length) {
                        const inquiryInput: InquiryInput[] = inquiryData[type].map((selectedItem, index) => {
                            let inquiryPayload;
                            if (type === PEOPLE_TYPE) {
                                selectedItem.contact = {
                                    contactInfo: selectedItem.contactInfo
                                } as ContactInput;
                                inquiryPayload = personInquiryPayload(inquiryData, selectedItem);
                            } else {
                                const activities = inquiryData?.activities.length
                                    ? inquiryData.activities.map((activity) => {
                                          return {
                                              name: activity.label,
                                              reference: activity
                                          };
                                      })
                                    : [];
                                inquiryPayload = orgInquiryPayload(inquiryData, selectedItem, activities);
                            }
                            return {
                                shortlistLineItemId: saveLineItems[index]?.id,
                                inquiryPayload
                            };
                        }) as InquiryInput[];
                        resolve(inquiryInput);
                    }
                })
                .catch(() => {
                    failedToastMessage('shortlist.line.items.save.error');
                    reject();
                });
        });
    };
    return { saveShorlistLineitems };
};

export const createResourceForProduction = () => {
    const [saveProductionResource] = useMutationWithContext(saveResourceQuery, productionClient);

    const saveResourceForProduction = (productionId: string, resInput: ResourceInput) => {
        return new Promise((resolve, reject) => {
            saveProductionResource({ variables: { input: resInput, productionId } })
                .then((saveResourceResponse) => {
                    if (saveResourceResponse.data.saveResource?.errors) {
                        failedToastMessage('resource.save.failure');
                        reject(saveResourceResponse.data.saveProduction.errors);
                    } else if (saveResourceResponse.data.saveResource?.id) {
                        resolve({
                            id: saveResourceResponse.data.saveResource.id,
                            reference: saveResourceResponse.data.saveResource.reference
                        });
                    }
                })
                .catch((err) => {
                    reject(err);
                });
        });
    };

    return { saveResourceForProduction };
};

export const handleEntitiesToProductionMutations = () => {
    const { application } = useContext(UserContext);
    const { changeRoute } = useHistory();
    const { saveResourceForProduction } = createResourceForProduction();
    const { saveShortlist } = useSaveShortlist();
    const { saveShorlistLineitems } = createShortlistLineitems();
    const { sendInquiriesToInvitee } = sendInquiryForShortlists();

    const associateEntitiesWithStepperProduction = ({ productionId, resData, inquiryData, onCancelStepper, type }) => {
        const resInput = toInputForSaveResource(CONSTANTS_ID.createId, resData) as ResourceInput;
        saveResourceForProduction(productionId, resInput).then((resourceData: { id: string; reference: Reference }) => {
            const resourceInfo: ResourceInfoForShortlist = {
                resourceInput: resInput,
                savedResourceRef: resourceData.reference
            };
            const shortlistInput = resourceStateToShortlist(CONSTANTS_ID.createId, resourceInfo) as ShortlistInput;
            inquiryData.resource = { ...resourceData.reference, label: inquiryData.resource.label };
            saveShortlist({ input: shortlistInput, showMessages: false }).then((res: MutationResult) => {
                const shortlistId = res.id;
                saveShorlistLineitems(shortlistId, inquiryData, type).then((inquiryInput: InquiryInput[]) => {
                    sendInquiriesToInvitee(inquiryInput).finally(() => {
                        onCancelStepper();
                        return changeRoute(
                            `${application.home}/workspace/${productionId}/resource/${resourceData.id}/details`
                        );
                    });
                });
            });
        });
    };

    return { associateEntitiesWithStepperProduction };
};
